import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, Shapes, BarChart3, ShieldCheck, Settings,
  Search, Moon, Sun, ChevronRight, CloudOff, BookOpen,
} from 'lucide-react';
import { useTheme } from '../lib/theme';
import { useConnectivity } from '../lib/connectivity';
import { Avatar } from './ui';
import { initials, ROLE_LABEL } from '../lib/format';
import type { CurrentUser } from '../api/types';
import { CommandPalette } from './CommandPalette';
import { AboutModal } from './AboutModal';
import { WhatsNewModal } from './WhatsNewModal';
import { DatenschutzModal, ImpressumModal } from './LegalModals';

const NAV = [
  { id: 'dashboard', to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { id: 'members', to: '/mitglieder', label: 'Mitglieder', icon: Users, end: false },
  { id: 'sections', to: '/sektionen', label: 'Sektionen', icon: Shapes, end: false },
  { id: 'stats', to: '/statistik', label: 'Statistik', icon: BarChart3, end: false },
] as const;

const APP_VERSION = '1.2.0';
const APP_DATE = 'Juli 2026';
const WHATS_NEW_KEY = `mvg_seen_whatsnew_${APP_VERSION}`;

export function AppShell({ user, children }: { user: CurrentUser; children: ReactNode }) {
  const { theme, toggle } = useTheme();
  const online = useConnectivity();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const [datenschutzOpen, setDatenschutzOpen] = useState(false);
  const [impressumOpen, setImpressumOpen] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();

  // ⌘K / Ctrl+K → Command Palette
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((s) => !s);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Show whats-new once per major version
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!localStorage.getItem(WHATS_NEW_KEY)) {
      const t = setTimeout(() => setWhatsNewOpen(true), 800);
      return () => clearTimeout(t);
    }
    return undefined;
  }, []);

  const dismissWhatsNew = (forever?: boolean) => {
    if (forever) localStorage.setItem(WHATS_NEW_KEY, '1');
    setWhatsNewOpen(false);
  };

  const breadcrumbs = breadcrumbsFor(loc.pathname);

  return (
    <div className="app-shell">
      {/* --- Sidebar (Desktop) --- */}
      <aside className="sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <img src="/logo.png" alt="Union Gaspoltshofen" />
          </div>
          <div className="sidebar-org">
            <span className="sidebar-org-name">Union Gaspoltshofen</span>
            <span className="sidebar-org-sub">Mitgliederverwaltung</span>
          </div>
        </div>

        <button className="sidebar-search" onClick={() => setPaletteOpen(true)}>
          <Search size={14} />
          <span>Suchen</span>
          <span className="kbd">⌘K</span>
        </button>

        <nav className="sidebar-nav scroll-area">
          <div className="sidebar-section">
            {NAV.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.id}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
                >
                  <span className="icon-wrap"><Icon size={17} /></span>
                  <span>{item.label}</span>
                </NavLink>
              );
            })}
          </div>

          <div className="sidebar-section">
            <div className="sidebar-section-label">Verwaltung</div>
            {user.rolle === 'admin' && (
              <NavLink to="/benutzer" className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}>
                <span className="icon-wrap"><ShieldCheck size={17} /></span>
                <span>Benutzer</span>
              </NavLink>
            )}
            <NavLink to="/einstellungen" className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}>
              <span className="icon-wrap"><Settings size={17} /></span>
              <span>Einstellungen</span>
            </NavLink>
            <NavLink to="/anleitung" className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}>
              <span className="icon-wrap"><BookOpen size={17} /></span>
              <span>Anleitung</span>
            </NavLink>
          </div>
        </nav>

        <div className="sidebar-footer">
          <button className="sidebar-item" onClick={toggle} style={{ marginBottom: 4 }}>
            <span className="icon-wrap">{theme === 'dark' ? <Sun size={17} /> : <Moon size={17} />}</span>
            <span>{theme === 'dark' ? 'Helles Design' : 'Dunkles Design'}</span>
          </button>
          <button className="sidebar-user" onClick={() => nav('/einstellungen')} type="button">
            <Avatar name={initials(user.vorname, user.nachname)} size="sm" />
            <div className="meta">
              <span className="name">{user.vorname} {user.nachname}</span>
              <span className="role">{ROLE_LABEL[user.rolle]}</span>
            </div>
            <ChevronRight size={14} />
          </button>
        </div>
      </aside>

      {/* --- Main column --- */}
      <div className="main-col">
        {/* Mobile topbar */}
        <div className="mobile-topbar">
          <div className="sidebar-logo" style={{ width: 30, height: 30 }}>
            <img src="/logo.png" alt="" style={{ width: 24, height: 24 }} />
          </div>
          <div className="org" style={{ flex: 1, minWidth: 0 }}>
            <span className="name" style={{ fontSize: 15, fontWeight: 600 }}>Union Gaspoltshofen</span>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={() => setPaletteOpen(true)} aria-label="Suchen">
            <Search size={18} />
          </button>
          <button className="btn btn-ghost btn-icon" onClick={toggle} aria-label="Design wechseln">
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </div>

        {/* Desktop topbar */}
        <div className="topbar">
          <div className="topbar-breadcrumb">
            {breadcrumbs.map((b, i) => (
              <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {i > 0 && <span className="sep"><ChevronRight size={12} /></span>}
                <span className={i === breadcrumbs.length - 1 ? 'crumb-current' : ''}>{b}</span>
              </span>
            ))}
          </div>
        </div>

        {!online && (
          <div className="offline-banner">
            <CloudOff size={15} />
            <span>Du bist offline. Bestehende Daten bleiben lesbar – zum Speichern brauchst du wieder eine Internetverbindung.</span>
          </div>
        )}

        <main className="page scroll-area">
          <div className="page-inner">{children}</div>
        </main>

        <footer className="app-footer">
          <button className="foot-version" onClick={() => setAboutOpen(true)} title="Über die App">
            v{APP_VERSION} · Stand {APP_DATE}
          </button>
          <span className="foot-sep">·</span>
          <button onClick={() => setWhatsNewOpen(true)}>Was ist neu</button>
          <span className="foot-sep">·</span>
          <button onClick={() => nav('/anleitung')}>Hilfe</button>
          <div className="spacer" />
          <button onClick={() => setDatenschutzOpen(true)}>Datenschutz</button>
          <span className="foot-sep">·</span>
          <button onClick={() => setImpressumOpen(true)}>Impressum</button>
          <span className="foot-sep">·</span>
          <span className="conn" title={online ? 'Verbunden' : 'Keine Verbindung'}>
            <span className="conn-dot" style={{ background: online ? 'var(--success)' : 'var(--danger)' }} />
            {online ? 'Verbunden' : 'Offline'}
          </span>
        </footer>

        {/* Bottom tabs (mobile) */}
        <nav className="bottom-tabs">
          {NAV.map((item) => {
            const Icon = item.icon;
            const active = loc.pathname === item.to || (item.to !== '/' && loc.pathname.startsWith(item.to));
            return (
              <button key={item.id} className={active ? 'active' : ''} onClick={() => nav(item.to)}>
                <span className="tab-ico"><Icon size={21} /></span>
                <span className="tab-lbl">{item.label}</span>
              </button>
            );
          })}
          <button className={loc.pathname.startsWith('/einstellungen') ? 'active' : ''} onClick={() => nav('/einstellungen')}>
            <span className="tab-ico"><Settings size={21} /></span>
            <span className="tab-lbl">Mehr</span>
          </button>
        </nav>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <AboutModal open={aboutOpen} onClose={() => setAboutOpen(false)} />
      <WhatsNewModal open={whatsNewOpen} onClose={() => dismissWhatsNew(false)} onDismissForever={() => dismissWhatsNew(true)} />
      <DatenschutzModal open={datenschutzOpen} onClose={() => setDatenschutzOpen(false)} />
      <ImpressumModal open={impressumOpen} onClose={() => setImpressumOpen(false)} />
    </div>
  );
}

function breadcrumbsFor(path: string): string[] {
  if (path === '/' || path === '') return ['Dashboard'];
  const seg = path.split('/').filter(Boolean);
  const map: Record<string, string> = {
    mitglieder: 'Mitglieder',
    sektionen: 'Sektionen',
    statistik: 'Statistik',
    benutzer: 'Benutzer',
    einstellungen: 'Einstellungen',
  };
  return seg.map((s) => map[s] ?? s);
}
